import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import zelleQR from "../images/zelle.png";

const Payment = () => {
  const [searchParams] = useSearchParams();
  const queryUserType = searchParams.get("userType"); // Get "returning" or "new" from URL
  const [userType, setUserType] = useState(queryUserType); // Initialize from URL param or null
  const [selectedOption, setSelectedOption] = useState(null);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paypalError, setPaypalError] = useState(null);
  const [showZelleQR, setShowZelleQR] = useState(false);

  // Load PayPal SDK when monthly option is selected
  useEffect(() => {
    let isComponentMounted = true;

    // Determine which plan ID based on selected option
    const getPayPalPlanId = () => {
      if (selectedOption === "monthly") {
        return "P-81U39121MX938944GNEXEE3Y"; // New student
      } else if (selectedOption === "monthly-returning") {
        return "P-8MW76576G31073818NEXEKXA"; // Returning student
      }
      return null;
    };

    const planId = getPayPalPlanId();
    const containerId = `paypal-button-container-${planId}`;

    const clearPayPalContainer = () => {
      const container = document.getElementById(containerId);
      if (container) {
        container.innerHTML = "";
      }
    };

    const renderPayPalButton = () => {
      if (!isComponentMounted) return;

      const container = document.getElementById(containerId);
      if (!container || !window.paypal) return;

      try {
        clearPayPalContainer();

        window.paypal
          .Buttons({
            style: {
              shape: "rect",
              color: "gold",
              layout: "vertical",
              label: "subscribe",
            },
            createSubscription: function (_, actions) {
              return actions.subscription.create({
                plan_id: planId,
              });
            },
            onApprove: function (data) {
              alert(data.subscriptionID);
            },
            onError: function (err) {
              console.error("PayPal error:", err);
              setPaypalError("Payment failed. Please try again.");
            },
          })
          .render(`#${containerId}`);

        if (isComponentMounted) {
          setPaypalLoading(false);
          setPaypalError(null);
        }
      } catch (error) {
        console.error("Error rendering PayPal button:", error);
        if (isComponentMounted) {
          setPaypalLoading(false);
          setPaypalError(
            "Failed to load payment options. Please refresh and try again.",
          );
        }
      }
    };

    if (
      selectedOption === "monthly" ||
      selectedOption === "monthly-returning"
    ) {
      setPaypalLoading(true);
      setPaypalError(null);

      // Check if PayPal script is already loaded
      if (!document.querySelector('script[src*="paypal.com/sdk/js"]')) {
        const script = document.createElement("script");
        script.src =
          "https://www.paypal.com/sdk/js?client-id=AfBDjIUH2jWON4wpXm3afe-9EoBqcjLc0XC0pX_43DN1li3YyqxtKTlpKfw6bvH4R5qfSxaZJIwoq2yH&vault=true&intent=subscription";
        script.setAttribute("data-sdk-integration-source", "button-factory");

        script.onload = () => {
          if (isComponentMounted) {
            setTimeout(renderPayPalButton, 100);
          }
        };

        script.onerror = () => {
          if (isComponentMounted) {
            setPaypalLoading(false);
            setPaypalError(
              "Failed to load PayPal. Please check your internet connection and try again.",
            );
          }
        };

        document.head.appendChild(script);
      } else {
        // Script already loaded, render button after short delay
        setTimeout(renderPayPalButton, 100);
      }
    } else {
      // Clear containers when switching away from monthly options
      document
        .getElementById("paypal-button-container-P-81U39121MX938944GNEXEE3Y")
        ?.remove();
      document
        .getElementById("paypal-button-container-P-8MW76576G31073818NEXEKXA")
        ?.remove();
      setPaypalLoading(false);
      setPaypalError(null);
    }

    // Cleanup function
    return () => {
      isComponentMounted = false;
      const container = document.getElementById(containerId);
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [selectedOption]);

  const pricingOptions = [
    {
      id: "monthly",
      title: "Monthly Plan – New Student",
      description: "$99/month for 3 months",
      registrationFee: 25,
      processingFee: 9.99,
      monthlyRate: 99,
      duration: 3,
      total: 331.99,
      isReturning: false,
      subtext:
        "$99/month for 3 months + $25 registration fee.\nOnline payments include processing fees:\n• $129.01 today\n• $102.94 per month for the next 2 months.",
      features: [
        "$99/month for 3 months",
        "$25 registration fee",
        "First payment: $129.01 (includes processing)",
        "Following months: $102.94 each",
      ],
    },
    {
      id: "upfront",
      title: "Pay In Full – $333.95",
      description: "New Student",
      registrationFee: 25,
      processingFee: 9.99,
      monthlyRate: 99,
      duration: 3,
      total: 333.95,
      isReturning: false,
      savings: 0,
      subtext:
        "Covers 3 months of classes at $99/month + $25 registration fee.\nOnline total includes processing fee: $333.95.",
      features: [
        "3 months of classes ($99/month)",
        "$25 registration fee",
        "Processing fee included",
        "No monthly payment hassle",
        "Pay once, all set",
      ],
    },
    {
      id: "monthly-returning",
      title: "Monthly Plan – Returning Student",
      description: "$99/month for 3 months",
      registrationFee: 0,
      processingFee: 9.99,
      monthlyRate: 99,
      duration: 3,
      total: 306.99,
      isReturning: true,
      subtext:
        "$99/month for 3 months.\nOnline payments include processing fee:\n• $102.94 per month for 3 months.\nNo registration fee for returning students.",
      features: [
        "$99/month for 3 months",
        "No registration fee",
        "Processing fee included",
        "$102.94 per month (including fees)",
        "Flexible monthly payments",
      ],
    },
    {
      id: "upfront-returning",
      title: "Pay In Full – $308.83",
      description: "Returning Student",
      registrationFee: 0,
      processingFee: 9.99,
      monthlyRate: 99,
      duration: 3,
      total: 308.83,
      isReturning: true,
      savings: 0,
      subtext:
        "Covers 3 months of classes at $99/month.\nOnline total includes processing fee: $308.83.\nNo registration fee for returning students.",
      features: [
        "3 months of classes ($99/month)",
        "No registration fee",
        "Processing fee included",
        "No monthly payment hassle",
        "Welcome back special",
      ],
    },
  ];

  const handleOptionSelect = (optionId) => {
    setSelectedOption(optionId);
  };

  const handleContinue = () => {
    if (selectedOption === "upfront") {
      // Open PayPal payment link for new student full payment
      window.open("https://www.paypal.com/ncp/payment/MFKRFW42G3FAQ", "_blank");
    } else if (selectedOption === "upfront-returning") {
      // Open PayPal payment link for returning student full payment
      window.open("https://www.paypal.com/ncp/payment/DPCUXSZJ8T6HY", "_blank");
    } else if (selectedOption) {
      // For other payment options (if any are added in the future)
      console.log("Selected option:", selectedOption);
      alert("Payment processing will be implemented in the next step!");
    }
  };

  // Show selection screen if no user type selected
  if (!userType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex flex-col">
        {/* Navigation spacer */}
        <div className="h-20"></div>

        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 text-center">
            Are you a <span className="text-purple-400">new</span> or{" "}
            <span className="text-teal-400">returning</span> student?
          </h1>
          <p className="text-xl text-gray-300 mb-16 text-center">
            Select below to see your pricing options
          </p>

          <div className="grid md:grid-cols-2 gap-8 w-full max-w-2xl">
            {/* New Student Button */}
            <button
              onClick={() => setUserType("new")}
              className="h-64 md:h-72 bg-gradient-to-br from-purple-600 to-purple-800 hover:from-purple-500 hover:to-purple-700 rounded-2xl p-8 transition-all duration-300 transform hover:scale-105 shadow-2xl border-2 border-purple-400"
            >
              <div className="flex flex-col items-center justify-center h-full">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  New Student
                </h2>
                <p className="text-lg text-purple-100">Get started with us</p>
              </div>
            </button>

            {/* Returning Student Button */}
            <button
              onClick={() => setUserType("returning")}
              className="h-64 md:h-72 bg-gradient-to-br from-teal-600 to-teal-800 hover:from-teal-500 hover:to-teal-700 rounded-2xl p-8 transition-all duration-300 transform hover:scale-105 shadow-2xl border-2 border-teal-400"
            >
              <div className="flex flex-col items-center justify-center h-full">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  Returning Student
                </h2>
                <p className="text-lg text-teal-100">Welcome back!</p>
              </div>
            </button>
          </div>
        </div>

        {/* Footer spacer */}
        <div className="h-12"></div>
      </div>
    );
  }

  // Filter pricing options based on user type
  const filteredOptions = pricingOptions.filter((option) =>
    userType === "new" ? !option.isReturning : option.isReturning,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Navigation spacer */}
      <div className="h-20"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header with Change Selection Button */}
        <div className="flex justify-between items-start mb-12">
          <Link
            to="/"
            className="inline-block text-white hover:text-purple-400 transition-colors"
          >
            ← Back to Home
          </Link>

          <button
            onClick={() => {
              setUserType(null);
              setSelectedOption(null);
            }}
            className={`inline-block transition-colors px-4 py-2 border rounded-lg ${
              userType === "new"
                ? "text-purple-400 hover:text-purple-300 border-purple-400 hover:border-purple-300"
                : "text-teal-400 hover:text-teal-300 border-teal-400 hover:border-teal-300"
            }`}
          >
            ← Change Selection
          </button>
        </div>

        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="mb-6 inline-block">
            <span
              className={`text-lg font-semibold px-4 py-2 rounded-full ${
                userType === "new"
                  ? "bg-purple-400/20 text-purple-300"
                  : "bg-teal-400/20 text-teal-300"
              }`}
            >
              {userType === "new" ? "New Student" : "Returning Student"}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Join <span className="text-purple-400">THE KITCHEN</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Choose your payment plan for 3 months of hip-hop dance classes
          </p>
        </div>

        {/* Zelle Payment Option */}
        <div className="mb-12 max-w-2xl mx-auto p-6 bg-gray-800/50 rounded-lg border border-gray-700">
          <p className="text-center text-gray-300 mb-4 whitespace-pre-line">
            Prefer to avoid processing fees? You can pay your tuition through
            Zelle. Simply scan the QR code and contact us by email or phone to
            complete your payment.
          </p>
          <div className="text-center">
            <button
              onClick={() => setShowZelleQR(!showZelleQR)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              {showZelleQR ? "Hide" : "Show"} Zelle QR Code
            </button>
          </div>
          {showZelleQR && (
            <div className="mt-6 flex justify-center">
              <img
                src={zelleQR}
                alt="Zelle QR Code"
                className="w-64 h-64 border-4 border-gray-600 rounded-lg"
              />
            </div>
          )}
        </div>

        {/* Pricing Options */}
        <div className="mb-16">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {filteredOptions.map((option) => {
              const isNewStudent = userType === "new";
              const accentColor = isNewStudent ? "purple" : "teal";
              const bgColor = isNewStudent
                ? "bg-gray-800/50"
                : "bg-gradient-to-br from-teal-900/30 to-teal-900/30";
              const borderColor = isNewStudent
                ? "border-gray-700 hover:border-purple-500"
                : "border-teal-700 hover:border-teal-500";
              const selectedBorder = isNewStudent
                ? "border-purple-400 ring-4 ring-purple-400/20"
                : "border-teal-400 ring-4 ring-teal-400/20";
              const checkmarkBg = isNewStudent
                ? "bg-purple-400"
                : "bg-teal-400";
              const textColor = isNewStudent
                ? "text-purple-400"
                : "text-teal-400";
              const descColor = isNewStudent
                ? "text-gray-400"
                : "text-teal-300 font-semibold";
              const buttonColor = isNewStudent
                ? "bg-purple-600 hover:bg-purple-700"
                : "bg-teal-600 hover:bg-teal-700";

              return (
                <div
                  key={option.id}
                  className={`relative ${bgColor} backdrop-blur-sm rounded-xl p-8 transition-all duration-300 cursor-pointer border-2 ${
                    selectedOption === option.id ? selectedBorder : borderColor
                  } transform ${selectedOption === option.id ? "scale-105" : ""}`}
                  onClick={() => handleOptionSelect(option.id)}
                >
                  {selectedOption === option.id && (
                    <div
                      className={`absolute top-4 right-4 ${checkmarkBg} text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold`}
                    >
                      ✓
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {option.title}
                    </h3>
                    <p className={descColor}>{option.description}</p>
                  </div>

                  <div className="text-center mb-6">
                    <div className="text-gray-300 whitespace-pre-line text-sm leading-relaxed">
                      {option.subtext}
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {option.features.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center text-gray-300"
                      >
                        <span className={`${textColor} mr-3`}>✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                      selectedOption === option.id
                        ? `${buttonColor} text-white`
                        : "bg-gray-700 hover:bg-gray-600 text-gray-300"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionSelect(option.id);
                    }}
                  >
                    {selectedOption === option.id ? "Selected" : "Select Plan"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* PayPal Button for Monthly Options */}
        {(selectedOption === "monthly" ||
          selectedOption === "monthly-returning") && (
          <div className="text-center mb-12">
            <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Complete Your Monthly Subscription
              </h3>

              {paypalLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Loading PayPal...</span>
                </div>
              )}

              {paypalError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800 text-sm">{paypalError}</p>
                  <button
                    onClick={() => {
                      setPaypalError(null);
                      setSelectedOption(null);
                      setTimeout(
                        () =>
                          setSelectedOption(
                            userType === "new"
                              ? "monthly"
                              : "monthly-returning",
                          ),
                        100,
                      );
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {selectedOption === "monthly" && (
                <div id="paypal-button-container-P-81U39121MX938944GNEXEE3Y"></div>
              )}
              {selectedOption === "monthly-returning" && (
                <div id="paypal-button-container-P-8MW76576G31073818NEXEKXA"></div>
              )}
            </div>
          </div>
        )}

        {/* Continue Button for Other Options */}
        {selectedOption &&
          selectedOption !== "monthly" &&
          selectedOption !== "monthly-returning" && (
            <div className="text-center">
              <button
                onClick={handleContinue}
                className="px-8 py-4 rounded-lg text-xl font-bold transition-all duration-300 bg-purple-600 hover:bg-purple-700 text-white transform hover:scale-105 shadow-xl"
              >
                Continue to Payment
              </button>
            </div>
          )}

        {/* Show Continue Button When No Option Selected */}
        {!selectedOption && (
          <div className="text-center">
            <button
              onClick={handleContinue}
              disabled={true}
              className="px-8 py-4 rounded-lg text-xl font-bold transition-all duration-300 bg-gray-600 text-gray-400 cursor-not-allowed"
            >
              Continue to Payment
            </button>
          </div>
        )}

        {/* Additional Info */}
        <div className="mt-12 text-center text-gray-400">
          <p className="mb-2">Questions about our pricing?</p>
          <Link
            to="/"
            className="text-purple-400 hover:text-purple-300 transition-colors"
          >
            Contact us for more information
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Payment;
